/**
 * StudioSwift by WRG - Global JavaScript
 * This file is loaded on all pages
 */

(function () {
  'use strict';

  /**
   * Debounce utility function
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function}
   */
  function debounce(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Trap focus within an element (for modals, drawers)
   * @param {HTMLElement} element - Element to trap focus within
   */
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
  }

  /**
   * Remove focus trap
   * @param {HTMLElement} element - Element to remove trap from
   */
  function removeTrapFocus(element) {
    const clone = element.cloneNode(true);
    element.parentNode.replaceChild(clone, element);
  }

  /**
   * Pause all media (videos) when not visible
   */
  function handleMediaVisibility() {
    const videos = document.querySelectorAll('video[autoplay]');

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.play();
          } else {
            entry.target.pause();
          }
        });
      }, { threshold: 0.5 });

      videos.forEach(video => observer.observe(video));
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    handleMediaVisibility();
  });

  window.StudioSwift = {
    debounce,
    trapFocus,
    removeTrapFocus
  };

})();


// Search functionality with ARIA support and Predictive Search API
(function () {
  'use strict';

  var MAX_VISIBLE_RESULTS = 5;

  function initSearch() {
    var searchBtns = document.querySelectorAll(".js-search-btn");
    var siteSearch = document.querySelector(".js-site-search");
    var searchCloseBtn = document.querySelector(".js-search-close");
    var searchInput = document.getElementById("site-search-input");
    var searchResults = document.querySelector(".site-search__results");
    var clearBtn = document.getElementById("site-search-clear");
    var searchFooter = document.querySelector(".site-search__footer");
    var viewAllLink = searchFooter ? searchFooter.querySelector("a") : null;

    if (!searchBtns.length || !siteSearch) return;

    var currentSearchBtn = null;
    var searchTimeout = null;

    function debounce(fn, delay) {
      return function () {
        var args = arguments;
        var context = this;
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(function () {
          fn.apply(context, args);
        }, delay);
      };
    }

    function showViewAll(show) {
      if (searchFooter) {
        searchFooter.style.display = show ? 'block' : 'none';
      }
    }

    function openSearch(clickedBtn) {
      currentSearchBtn = clickedBtn;
      searchBtns.forEach(function (btn) {
        btn.setAttribute("aria-expanded", "true");
      });
      siteSearch.classList.add("is-open");
      siteSearch.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";
      showViewAll(false);

      if (searchInput) {
        setTimeout(function () {
          searchInput.focus();
        }, 300);
      }
    }

    function closeSearch() {
      searchBtns.forEach(function (btn) {
        btn.setAttribute("aria-expanded", "false");
      });
      siteSearch.classList.remove("is-open");
      siteSearch.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      showViewAll(false);

      if (searchResults) {
        searchResults.innerHTML = '<ul></ul>';
      }
      if (searchInput) {
        searchInput.value = "";
      }
      if (currentSearchBtn) {
        currentSearchBtn.focus();
        currentSearchBtn = null;
      }
    }

    function toggleSearch(clickedBtn) {
      if (siteSearch.classList.contains("is-open")) {
        closeSearch();
      } else {
        openSearch(clickedBtn);
      }
    }

    function performSearch(query) {
      if (!query || query.length < 2) {
        searchResults.innerHTML = '<ul></ul>';
        showViewAll(false);
        return;
      }

      searchResults.innerHTML = '<div class="site-search__loading">Searching...</div>';
      showViewAll(false);

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');

      fetch(root + 'search/suggest.json?' + params.toString())
        .then(function (response) {
          if (!response.ok) throw new Error('Search failed');
          return response.json();
        })
        .then(function (data) {
          var results = (data.resources && data.resources.results) || data.resources || {};
          renderResults(results, query);
        })
        .catch(function (error) {
          console.error('Search error:', error);
          searchResults.innerHTML = '<div class="site-search__error">Search unavailable. Please try again.</div>';
          showViewAll(false);
        });
    }

    function renderResults(results, query) {
      var products = results.products || [];
      var viewProduct = (siteSearch && siteSearch.dataset.viewProduct) || 'View product';

      if (products.length === 0) {
        searchResults.innerHTML = '<div class="ss search-result search-result--no-results"><h3>No results found</h3><p>Try adjusting your search terms</p></div>';
        showViewAll(false);
        return;
      }

      var html = '<ul>';
      for (var i = 0; i < products.length && i < MAX_VISIBLE_RESULTS; i++) {
        var product = products[i];
        var image = product.image ? product.image.replace(/\.([^.]+)$/, '_400x400.$1') : '';

        html += '<li class="ss search-result">';
        html += '<figure class="' + (image ? '' : 'search-result__image--placeholder') + '">';
        if (image) {
          html += '<img src="' + image + '" alt="' + escapeHtml(product.title) + '" width="135" height="135" loading="lazy">';
        }
        html += '</figure>';
        html += '<article>';
        html += '<h3>' + highlightMatch(product.title, query) + '</h3>';
        html += '<a href="' + (stripSearchTrackingParams(product.url) || '#') + '" class="search-result__link">' + escapeHtml(viewProduct) + '</a>';
        html += '</article>';
        html += '</li>';
      }

      html += '</ul>';
      searchResults.innerHTML = html;

      if (viewAllLink) {
        viewAllLink.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'search?q=' + encodeURIComponent(query);
        showViewAll(true);
      } else {
        showViewAll(false);
      }
    }

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function escapeHtml(text) {
      var div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    function stripSearchTrackingParams(url) {
      if (!url || typeof url !== 'string') return url || '';
      try {
        var idx = url.indexOf('?');
        if (idx === -1) return url;
        var path = url.slice(0, idx);
        var search = url.slice(idx + 1);
        var params = new URLSearchParams(search);
        var tracking = ['_pos', '_sid', '_psq', '_ss', '_v'];
        tracking.forEach(function (p) { params.delete(p); });
        var remaining = params.toString();
        return remaining ? path + '?' + remaining : path;
      } catch (e) {
        return url.split('?')[0] || url;
      }
    }

    function highlightMatch(text, query) {
      var escaped = escapeHtml(text);
      var safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var regex = new RegExp('(' + safeQuery + ')', 'gi');
      return escaped.replace(regex, '<mark>$1</mark>');
    }

    // Event listeners
    searchBtns.forEach(function (searchBtn) {
      searchBtn.addEventListener("click", function (event) {
        event.preventDefault();
        toggleSearch(searchBtn);
      });
      searchBtn.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleSearch(searchBtn);
        }
      });
    });

    if (searchCloseBtn) {
      searchCloseBtn.addEventListener("click", function (event) {
        event.preventDefault();
        closeSearch();
      });
      searchCloseBtn.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          closeSearch();
        }
      });
    }

    if (searchInput) {
      var debouncedSearch = debounce(function (e) {
        performSearch(e.target.value.trim());
      }, 300);

      searchInput.addEventListener("input", debouncedSearch);
      searchInput.addEventListener("input", function () {
        if (viewAllLink) {
          var query = searchInput.value.trim();
          viewAllLink.href = query ? '/search?q=' + encodeURIComponent(query) : '/search';
        }
      });
    }

    if (clearBtn && searchInput) {
      clearBtn.addEventListener("click", function () {
        searchInput.value = "";
        searchResults.innerHTML = '<ul></ul>';
        showViewAll(false);
        searchInput.focus();
      });
    }

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && siteSearch.classList.contains("is-open")) {
        closeSearch();
      }
    });

    document.addEventListener("click", function (event) {
      if (
        siteSearch.classList.contains("is-open") &&
        !event.target.closest(".js-site-search") &&
        !event.target.closest(".js-search-btn")
      ) {
        closeSearch();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearch);
  } else {
    initSearch();
  }
})();


// Cart count updater - listens for cart:updated events
(function () {
  'use strict';

  function updateCartCount(count) {
    var cartLinks = document.querySelectorAll('a[href*="/cart"]');
    cartLinks.forEach(function (link) {
      var text = link.textContent;
      var newText = text.replace(/\[\d+\]/, '[' + count + ']');
      if (newText !== text) {
        link.textContent = newText;
      }
    });
  }

  function fetchCartCount() {
    fetch('/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
      })
      .catch(function (error) {
        console.error('Failed to fetch cart:', error);
      });
  }

  // Listen for cart:updated event from add to cart
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && typeof e.detail.count !== 'undefined') {
      updateCartCount(e.detail.count);
    } else {
      fetchCartCount();
    }
  });
})();


//product add to cart disabled for personalized 

document.addEventListener("click", function (e) {
  const sizeBtn = e.target.closest(".product-card__size-btn");
  if (!sizeBtn) return;

  const card = sizeBtn.closest(".product-card");
  const addToCartBtn = card.querySelector(".product-card__add-to-cart");
  const productLink = card.querySelector(".product-card__title-link").href;

  const isPersonalized = sizeBtn.dataset.variantTitle
    .toLowerCase()
    .includes("personalized");

  if (isPersonalized) {
    addToCartBtn.disabled = true;

    // Temporarily override click to go to PDP
    addToCartBtn.onclick = function (ev) {
      ev.preventDefault();
      window.location.href = productLink;

    };
    const card = sizeBtn.closest(".product-card");
    const productLink = card.querySelector(".product-card__title-link").href;
    const variantId = sizeBtn.dataset.variantId;

    // stop any existing variant logic for this click only
    e.preventDefault();
    e.stopPropagation();

    // go to PDP with personalized variant selected
    window.location.href = `${productLink}?variant=${variantId}`;
  } else {
    // Re-enable Add-to-Cart for normal variants
    addToCartBtn.disabled = false;

    // Remove temporary click override
    addToCartBtn.onclick = null;

  }
});

//redirect to pdp when there are more than 6 variants with a "+" indicator

document.querySelectorAll(".product-card__sizes").forEach(function (sizesWrap) {
  const buttons = Array.from(sizesWrap.querySelectorAll(".product-card__size-btn"));
  const maxVisible = 6; // show only first 6 buttons

  if (buttons.length <= maxVisible) return; // nothing to hide

  // hide extra buttons
  buttons.forEach((btn, index) => {
    if (index >= maxVisible) {
      btn.style.display = "none";
    }
  });

  // find product link (PDP)
  const card = sizesWrap.closest(".product-card");
  const productLink = card.querySelector(".product-card__title-link").href;

  // create + button
  const moreBtn = document.createElement("button");
  moreBtn.type = "button";
  moreBtn.className = "product-card__size-btn product-card__size-btn--more";
  moreBtn.textContent = `+${buttons.length - maxVisible}`; // show number of hidden buttons

  moreBtn.addEventListener("click", function () {
    window.location.href = productLink;
  });

  sizesWrap.appendChild(moreBtn);
});


// header sticky on scroll 
document.addEventListener('scroll', function () {
  const header = document.querySelector('.ss.header');

  if (!header) return;

  if (window.scrollY > 10) {
    header.classList.add('is-scrolled');
  } else {
    header.classList.remove('is-scrolled');
  }
});
